import { ReactNode, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Tabs,
  TabsList,
  TabsTrigger,
  V2Loader,
  VisuallyHidden,
} from "web-design-system";
import { usePortfolioReviewExposure } from "creso-core/hooks";
import { PortfolioReview, ReviewOverlapPair } from "creso-core/types";
import { formatAsPercentage } from "creso-core/utils";
import { AmcLogo } from "@core/components/logo/amc-logo";
import {
  AmcFundExposure,
  HoldingsExposure,
} from "@/modules/reports/components/portfolio-review/exposure-pages";
import { useAmcNames } from "@/modules/reports/components/portfolio-review/report-data";

const TABS = [
  { value: "amc", label: "AMC & Funds" },
  { value: "stocks", label: "Stocks" },
  { value: "sectors", label: "Sector" },
  { value: "overlaps", label: "Overlaps" },
] as const;

type ExposureTab = (typeof TABS)[number]["value"];

export function ExposureDialog({
  review,
  open,
  onOpenChange,
}: {
  review: PortfolioReview;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [tab, setTab] = useState<ExposureTab>("amc");
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[90vh] min-h-160 max-w-5xl flex-col gap-6 overflow-y-auto">
        <DialogHeader className="flex-row items-center justify-between gap-4 space-y-0">
          <VisuallyHidden>
            <DialogTitle>Exposure</DialogTitle>
          </VisuallyHidden>
          <span className="text-lg font-semibold">Exposure</span>
          <DialogDescription className="sr-only">
            Exposure of the current portfolio and after the review.
          </DialogDescription>
          <Tabs
            value={tab}
            onValueChange={(value) => setTab(value as ExposureTab)}
          >
            <TabsList>
              {TABS.map(({ value, label }) => (
                <TabsTrigger key={value} value={value} className="rounded-md">
                  {label}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        </DialogHeader>
        {/* Mounted only while open, so exposure is fetched on demand. */}
        {open ? <ExposureContent review={review} tab={tab} /> : null}
      </DialogContent>
    </Dialog>
  );
}

function ExposureContent({
  review,
  tab,
}: {
  review: PortfolioReview;
  tab: ExposureTab;
}) {
  const amcName = useAmcNames(review);
  // AMCs and funds are worked out live from the lines; stocks, sectors and
  // overlaps come from the exposure endpoint, fetched once any of them opens.
  const { data: exposure, isLoading } = usePortfolioReviewExposure(review.id, {
    enabled: tab !== "amc",
  });

  if (tab === "amc") {
    return (
      <AmcFundExposure
        review={review}
        snapshots={review.insights.exposure}
        amcName={amcName}
      />
    );
  }

  if (tab === "overlaps") {
    return (
      <V2Loader isLoading={isLoading}>
        <div className="flex flex-col gap-6">
          <OverlapList
            label="Current portfolio"
            pairs={exposure?.overlap.before.pairs ?? []}
          />
          <OverlapList
            label="After review"
            pairs={exposure?.overlap.after.pairs ?? []}
          />
        </div>
      </V2Loader>
    );
  }

  const holdings = tab === "stocks" ? exposure?.stocks : exposure?.sectors;
  const before = holdings?.before ?? [];
  const after = holdings?.after ?? [];
  return (
    <V2Loader isLoading={isLoading}>
      {before.length || after.length ? (
        <HoldingsExposure holdings={{ before, after }} />
      ) : (
        <EmptyNote>
          No {tab === "stocks" ? "stock" : "sector"} exposure to show
        </EmptyNote>
      )}
    </V2Loader>
  );
}

function OverlapList({
  label,
  pairs,
}: {
  label: string;
  pairs: ReviewOverlapPair[];
}) {
  return (
    <div className="flex flex-col gap-4">
      <span className="border-b border-border pb-4 text-sm font-medium text-muted-foreground">
        {label}
      </span>
      {pairs.length ? (
        pairs.map(({ fund_a, fund_b, overlap_pct }) => (
          <div
            key={`${fund_a.isin}-${fund_b.isin}`}
            className="grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-4 rounded-lg border border-border px-4 text-sm"
          >
            <span className="flex min-w-0 items-center gap-3 py-2">
              <AmcLogo amc_code={fund_a.amc_morningstar_code} />
              <span className="truncate">{fund_a.scheme_name}</span>
            </span>
            <span className="w-25 self-stretch content-center border-x border-border bg-card text-center font-semibold">
              {formatAsPercentage(overlap_pct, "-", 0)}
            </span>
            <span className="flex min-w-0 items-center justify-end gap-3 py-2">
              <span className="truncate">{fund_b.scheme_name}</span>
              <AmcLogo amc_code={fund_b.amc_morningstar_code} />
            </span>
          </div>
        ))
      ) : (
        <EmptyNote>No overlapping funds</EmptyNote>
      )}
    </div>
  );
}

function EmptyNote({ children }: { children: ReactNode }) {
  return (
    <p className="rounded-lg border border-border p-6 text-center text-sm text-muted-foreground">
      {children}
    </p>
  );
}
